import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Control, useWatch } from 'react-hook-form'
import { SuggestResult } from 'core/interfaces/dictionary-interfaces'
import api from 'api/thai2english-api'
import { useTranslit } from './useTranslit'
import {
   classifySuggestQuery,
   flattenGroups,
   SuggestGroup,
   SuggestItem,
   toSuggestGroups,
} from 'lib/suggest-groups'
import { SuggestSurface, trackSuggestNoResults } from 'lib/analytics/events'
import { T2EFormData } from './useTranslationForm'

// Long enough that a normal typing rhythm produces one request per word rather
// than one per letter; short enough that pausing feels like an answer arriving,
// not a page loading.
const DEBOUNCE_MS = 180

const EMPTY_RESULT: SuggestResult = {
   isThai: false,
   exactMatch: null,
   exactLang: null,
   thaiMatches: [],
   engMatches: [],
   translitMatches: [],
}

export type UseSuggestResult = {
   groups: SuggestGroup[]
   /** Every row across every group, in display order — what ↑↓ walks. */
   items: SuggestItem[]
   /** The query the groups describe, already normalised. */
   query: string
   isFetching: boolean
}

// How long a settled miss has to stand before it counts as a word someone
// actually asked for. Typing one missing word settles on its prefixes on the
// way past — those are keystrokes, not questions, and only the one left on
// screen is worth reporting.
const MISS_REPORT_MS = 1500

/**
 * Suggestions for whatever is currently in the search box.
 *
 * Reads the box through the form's own `control`, so a component already
 * wired to react-hook-form gains autocomplete without changing its props.
 */
const useSuggest = (control: Control<T2EFormData>, surface: SuggestSurface): UseSuggestResult => {
   const sourceText = useWatch({ control, name: 'sourceText' }) ?? ''
   const translit = useTranslit()

   // The last answer that actually arrived, and the query it answers — one piece
   // of state, replaced whole, never assembled from a response and a loading
   // flag. That matters for more than tidiness: an "is it still loading" input
   // into what the panel shows would blank the panel on every keystroke and let
   // it flash back a moment later, taking the backdrop and the field's corners
   // with it. The panel simply lags one settle behind the box, rows and empty
   // state alike, and never has an in-between state to render.
   const [settled, setSettled] = useState<{ query: string; result: SuggestResult } | null>(null)
   const [isFetching, setIsFetching] = useState(false)

   // Guards against an out-of-order response overwriting a newer one. Aborting
   // covers the common case, but a request that has already resolved can't be
   // aborted, so the sequence number is what actually makes this safe.
   const sequenceRef = useRef(0)
   const inFlightRef = useRef<AbortController | null>(null)

   const kind = classifySuggestQuery(sourceText)

   useEffect(() => {
      // Nothing worth asking about: drop any in-flight request and clear, so the
      // panel never shows results belonging to text that has been deleted.
      if (kind !== 'lookup') {
         inFlightRef.current?.abort()
         inFlightRef.current = null
         sequenceRef.current += 1
         setSettled(null)
         setIsFetching(false)
         return
      }

      const query = sourceText.trim()
      setIsFetching(true)

      const timer = setTimeout(() => {
         inFlightRef.current?.abort()
         const controller = new AbortController()
         inFlightRef.current = controller

         sequenceRef.current += 1
         const sequence = sequenceRef.current

         void api.suggest(query, controller.signal).then((next) => {
            // A newer query has been asked since; this answer is history.
            if (sequence !== sequenceRef.current) return
            setSettled({ query, result: next })
            setIsFetching(false)
         })
      }, DEBOUNCE_MS)

      return () => clearTimeout(timer)
   }, [sourceText, kind])

   const renderTranslit = useCallback((t2e: string) => translit.of(t2e), [translit])

   const groups = useMemo(
      () => toSuggestGroups(settled?.result ?? EMPTY_RESULT, settled?.query ?? '', renderTranslit),
      [settled, renderTranslit]
   )
   const items = useMemo(() => flattenGroups(groups), [groups])

   // A miss is never rendered — the panel only draws rows, so a query that
   // matched nothing simply doesn't open it — but it is still worth reporting.
   // Reads only settled state, so "nothing found" is never a guess made
   // mid-flight: it is an answer that arrived, about the query named alongside
   // it. A request that failed arrived too, but says nothing about the
   // dictionary, so it is excluded.
   const isEmptyResult =
      kind === 'lookup' && settled !== null && !settled.result.suggestFailed && items.length === 0
   const settledQuery = settled?.query ?? ''

   // A miss that is still on screen after a beat is a word the dictionary is
   // missing and someone went looking for. Typing on restarts the wait, so the
   // prefixes passed through on the way to it never report; the event itself
   // only fires once per query per session.
   useEffect(() => {
      if (!isEmptyResult) return
      const timer = setTimeout(() => trackSuggestNoResults(surface, settledQuery), MISS_REPORT_MS)
      return () => clearTimeout(timer)
   }, [isEmptyResult, settledQuery, surface])

   return {
      groups,
      items,
      query: settledQuery,
      isFetching,
   }
}

export default useSuggest
